import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["for"]
const _hoisted_2 = ["value"]
const _hoisted_3 = {
  key: 0,
  class: "col-12"
}
const _hoisted_4 = {
  key: 1,
  class: "col-12"
}
const _hoisted_5 = ["disabled"]

import { reactive, watchEffect, computed, ref, onMounted } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import { Schema } from "yup";
import { $api } from "@/services/api1";
import { MaskType } from "@/composables/maskingUtils";
import SchoolSearchSelect from "@/components/shared/SchoolSearchSelect.vue";
import  {
  type School,
  type ServiceType,
  DrawStatuses,
  DrawStatusesToAllowUpdateCertMethod,
  DrawStatusesToAllowUpdateLoanPeriodDates,
  type MinimumDrawAmountResponse,
  LoanDetails,
} from "@/models/loans";
import { useDrawData } from "@/composables/loan/useDrawData";
import useFormatter from "@/use/formatter";
import useFunctions from "@/use/functions1";
import CurrencyInput from "@/components/inputs/CurrencyInput.vue";
import ParentAttestationConsent from "@/components/v2/consents/ParentAttestationConsent.vue";
import { ConsentServiceEntityTypeIds } from "@/types/consents";
import { FormModes } from "@/types/form";

interface FormField {
  name: string;
  label: string;
  type: string;
  value: any;
  width: string;
  options?: any;
  showOnAdd: boolean;
  showOnEdit: boolean;
  disabledOnEdit: boolean;
  maskType?: MaskType;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DrawForm',
  props: {
    onSubmit: { type: Function },
    isSubmitting: { type: Boolean },
    fields: {},
    validationSchema: {},
    mode: {},
    programTypeId: {},
    productTypeId: {},
    lenderId: {},
    initialValues: {},
    loanId: {},
    minimumDrawAmount: {},
    loanDetails: {},
    draw: {}
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

const showOverdrawnWarning = ref(false);

// Template Refs
const parentAttestationConsentRef = ref<InstanceType<
  typeof ParentAttestationConsent
> | null>(null);

const schoolSearchSelectRef = ref<InstanceType<
  typeof SchoolSearchSelect
> | null>(null);

const formRef = ref<InstanceType<typeof Form> | null>(null);

const currencyInputRefs = ref<Array<InstanceType<typeof CurrencyInput>> | null>(
  null
);

const initialValuesProp = computed(() => {
  return mapDrawObject(props.initialValues);
});

const { formatDateInputDisplayValue, formatMoney } = useFormatter();
const { convertMoneyStringToNumber } = useFunctions();

const { getEnrollmentTermsAsync } = useDrawData(
  props.lenderId,
  props.productTypeId,
  props.programTypeId
);

const formData = reactive<Record<string, any>>({});

const formErrors = computed<
  Partial<Record<string, string | undefined>> | undefined
>(() => formRef.value?.errors || undefined);

const isFormValid = computed(() => {
  return (errors: Partial<Record<string, string | undefined>>) =>
    errors && Object.keys(errors).length === 0;
});

const getCurrencyMask = (value) => {
  let cleanedValue =
    typeof value === "string" ? convertMoneyStringToNumber(value) : value;
  let formattedValue = !isNaN(cleanedValue)
    ? formatMoney(cleanedValue, 2)
    : null;

  return {
    value: value,
    cleanedValue: cleanedValue,
    formattedValue: formattedValue,
  };
};

const requestedAmount = computed(() => {
  const requestedAmount = formData?.requestedAmount;
  let cleanedValue =
    typeof requestedAmount === "string"
      ? convertMoneyStringToNumber(requestedAmount)
      : requestedAmount;
  let formattedValue = !isNaN(cleanedValue)
    ? formatMoney(cleanedValue, 2)
    : null;

  return {
    value: formData?.requestedAmount,
    cleanedValue: cleanedValue,
    formattedValue: formattedValue,
  };
});

const certifiedAmount = computed(() => {
  const certifiedAmount = formData?.certifiedAmount;
  let cleanedValue =
    typeof certifiedAmount === "string"
      ? convertMoneyStringToNumber(certifiedAmount)
      : certifiedAmount;
  let formattedValue = !isNaN(cleanedValue) ? formatMoney(cleanedValue) : null;

  return {
    value: formData?.requestedAmount,
    cleanedValue: cleanedValue,
    formattedValue: formattedValue,
  };
});

const loadOverdrawnWarning = async () => {
  if (props.mode !== "add") {
    showOverdrawnWarning.value = false;
    return;
  }
  showOverdrawnWarning.value = !(await $api.loans.canRequestFundAsync(
    props.loanId
  ));
};

function isFieldDisabled(field: FormField) {
  const disabledOnEdit = props.mode === "edit" && field.disabledOnEdit === true;

  return props.mode === "view" || props.isSubmitting || disabledOnEdit;
}

function getField(name: string): FormField | undefined {
  return props.fields.find((f) => f.name == name);
}

function mapDrawObject(selectedDraw: any) {
  if (!selectedDraw) return null;

  const schoolSelect = schoolSearchSelectRef.value?.[0];
  const school = schoolSelect
    ? (selectedDraw?.schoolDetailsId
        ? schoolSelect.findSchoolWithDetailId(selectedDraw.schoolDetailsId)
        : undefined)
      ?? schoolSelect.findSchool(selectedDraw?.schoolCode, selectedDraw?.schoolBranch)
    : undefined;
  const certificationMethod = getField("certificationMethodId")?.options?.find(
    (o: ServiceType) => o.id === selectedDraw.certificationMethodId
  );
  const enrollmentStatus = getField("enrollmentStatus")?.options?.find(
    (o: ServiceType) => o.id === selectedDraw.enrollmentStatus
  );
  const enrollmentTerm = getField("enrollmentTermId")?.options?.find(
    (o: ServiceType) => o.id === selectedDraw.enrollmentTermId
  );
  const drawStatus = getField("drawStatusTypeId")?.options?.find(
    (o: ServiceType) => o.id === selectedDraw.drawStatusTypeId
  );
  const gradeLevel = getField("schoolLevelTypeId")?.options?.find(
    (o: ServiceType) => o.id === selectedDraw.schoolLevelTypeId
  );

  const requestedAmountFormatted = selectedDraw.requestedAmount
    ? formatMoney(selectedDraw.requestedAmount)
    : null;
  const certifiedAmountFormatted =
    selectedDraw.certifiedAmount || selectedDraw.certifiedAmount === 0
      ? formatMoney(selectedDraw.certifiedAmount)
      : null;

  return {
    ...selectedDraw,
    requestedAmountFormatted: requestedAmountFormatted,
    certifiedAmountFormatted: certifiedAmountFormatted,
    schoolName: school?.schoolName,
    schoolLevelTypeName: selectedDraw?.schoolLevelTypeName || gradeLevel?.name,
    certificationMethodTypeName: certificationMethod?.name,
    enrollmentStatusName: enrollmentStatus?.name,
    enrollmentTermName: enrollmentTerm?.name,
    drawStatusTypeName: drawStatus?.name,
    anticipatedGraduationDateDisplay: formatDateInputDisplayValue(
      selectedDraw.anticipatedGraduationDate
    ),
    loanPeriodStartDateDisplay: formatDateInputDisplayValue(
      selectedDraw.loanPeriodStartDate
    ),
    loanPeriodEndDateDisplay: formatDateInputDisplayValue(
      selectedDraw.loanPeriodEndDate
    ),
    createdAtUtc: formatDateInputDisplayValue(selectedDraw.createdAtUtc),
    createdBy: selectedDraw.createdBy,
  };
}

function handleFieldChange(field: FormField, value: any) {
  switch (field.name) {
    case "schoolLevelTypeId":
      formData.schoolLevelTypeName = field?.options?.find(
        (o: ServiceType) => o.id === Number(value)
      )?.name;
      break;
    case "certificationMethodId":
      formData.certificationMethodTypeName = field?.options?.find(
        (o: ServiceType) => o.id === Number(value)
      )?.name;
      break;
    case "enrollmentStatus":
      formData.enrollmentStatusName = field?.options?.find(
        (o: ServiceType) => o.id === Number(value)
      )?.name;
      break;
    case "enrollmentTermId":
      formData.enrollmentTermName = field?.options?.find(
        (o: ServiceType) => o.id === Number(value)
      )?.name;
      break;
    case "drawStatusTypeId": {
      formData.drawStatusTypeName = field?.options?.find(
        (o: ServiceType) => o.id === Number(value)
      )?.name;
      const cmField = getField("certificationMethodId");
      if (cmField) {
        cmField.disabledOnEdit = !DrawStatusesToAllowUpdateCertMethod.includes(
          Number(value) as DrawStatuses
        );
      }

      const allowUpdateLoanPeriod =
        DrawStatusesToAllowUpdateLoanPeriodDates.includes(
          Number(value) as DrawStatuses
        );
      for (const key of ["loanPeriodStartDate", "loanPeriodEndDate"] as const) {
        const f = getField(key);
        if (f) f.disabledOnEdit = !allowUpdateLoanPeriod;
      }
      break;
    }
    case "certifiedAmount":
      formData.certifiedAmountFormatted =
        getCurrencyMask(value)?.formattedValue;
      break;
    case "requestedAmount":
      formData.requestedAmountFormatted = formatMoney(
        typeof value === "string" ? convertMoneyStringToNumber(value) : value
      );
      break;
    case "anticipatedGraduationDate":
      formData.anticipatedGraduationDateDisplay =
        formatDateInputDisplayValue(value);
      break;
    case "loanPeriodStartDate":
      formData.loanPeriodStartDateDisplay = formatDateInputDisplayValue(value);
      break;
    case "loanPeriodEndDate":
      formData.loanPeriodEndDateDisplay = formatDateInputDisplayValue(value);
      break;
    case "cluid":
    case "sequenceNumber":
      break;
    default:
      break;
  }

	formRef.value?.validateField(field.name, value);
}

function discardChanges() {
  resetSchoolField();
  formRef.value?.resetForm();
}

function resetSchoolField() {
  if (schoolSearchSelectRef.value && schoolSearchSelectRef.value[0]) {
    const school =
      schoolSearchSelectRef.value && schoolSearchSelectRef.value[0]
        ? schoolSearchSelectRef.value[0].findSchool(
            initialValuesProp.value?.schoolCode,
            initialValuesProp.value?.schoolBranch
          )
        : undefined;

    schoolSearchSelectRef.value[0].discardChanges(school);
  }
}

async function handleSchoolSelect(selectedSchool: School) {
  formData.school = selectedSchool;

  formData.schoolName = selectedSchool.schoolName;

  // set ids, and code/branch from school
  formData.schoolId = selectedSchool.id;
  formData.schoolDetailsId = selectedSchool.schoolDetailsId;
  formData.schoolCode = selectedSchool.schoolDOEId;
  formData.schoolBranch = selectedSchool.schoolBranchDOEId;

  if (props.mode == "add" && selectedSchool.id) {
    const enrollmentTerms = await getEnrollmentTermsAsync(
      true,
      selectedSchool.id,
      null,
      null
    );
    const field = props.fields.find((f) => f.name == "enrollmentTermId");

    if (field && enrollmentTerms) {
      field.options = enrollmentTerms;
    }
  }
}

async function onSubmit() {
  if (props.onSubmit) {
    await props.onSubmit({ ...formData });
  }
}

onMounted(loadOverdrawnWarning);

watchEffect(() => {
  props.fields.forEach((field) => {
    formData[field.name] = field.value;
    handleFieldChange(field, field.value);
  });
});

__expose({
  formErrors,
  requestedAmount,
  certifiedAmount,
  currencyInputRefs,
	formData,
	parentAttestationConsentRef,
  schoolSearchSelectRef,
  initialValuesProp,
  discardChanges,
  validate: () => formRef.value?.validate(),
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Form), {
    ref_key: "formRef",
    ref: formRef,
    "validation-schema": __props.validationSchema,
    class: "form-container",
    onSubmit: onSubmit,
    "initial-values": initialValuesProp.value || undefined
  }, {
    default: _withCtx(({ errors }) => [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.fields, (field) => {
        return (_openBlock(), _createElementBlock("div", {
          key: field.name,
          class: _normalizeClass(['form-input', field.width])
        }, [
          _createElementVNode("label", {
            for: field.name
          }, _toDisplayString(field.label), 9, _hoisted_1),
          (field.type === 'school-select')
            ? (_openBlock(), _createBlock(_unref(Field), {
                key: 0,
                id: field.name,
                name: field.name,
                modelValue: formData[field.name],
                "onUpdate:modelValue": ($event: any) => ((formData[field.name]) = $event)
              }, {
                default: _withCtx(() => [
                  _createVNode(SchoolSearchSelect, {
                    ref_for: true,
                    ref_key: "schoolSearchSelectRef",
                    ref: schoolSearchSelectRef,
                    productTypeId: __props.productTypeId,
                    programTypeId: __props.programTypeId,
                    lenderId: __props.lenderId,
                    initialSchool: formData[field.name],
                    disabled: isFieldDisabled(field),
                    "onUpdate:selectedSchool": handleSchoolSelect
                  }, null, 8, ["productTypeId", "programTypeId", "lenderId", "initialSchool", "disabled"])
                ]),
                _: 2
              }, 1032, ["id", "name", "modelValue", "onUpdate:modelValue"]))
            : (field.type === 'select')
              ? (_openBlock(), _createBlock(_unref(Field), {
                  key: 1,
                  as: "select",
                  id: field.name,
                  name: field.name,
                  modelValue: formData[field.name],
                  "onUpdate:modelValue": [($event: any) => ((formData[field.name]) = $event), ($event: any) => (handleFieldChange(field, $event))],
                  disabled: isFieldDisabled(field)
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(field.options, (option) => {
                      return (_openBlock(), _createElementBlock("option", {
                        key: option.id,
                        value: option.id
                      }, _toDisplayString(option.name), 9, _hoisted_2))
                    }), 128))
                  ]),
                  _: 2
                }, 1032, ["id", "name", "modelValue", "onUpdate:modelValue", "disabled"]))
              : (field.type === 'money')
                ? (_openBlock(), _createBlock(_unref(Field), {
                    key: 2,
                    name: field.name,
                    disabled: isFieldDisabled(field),
                    mode: __props.mode,
                    modelValue: formData[field.name],
                    "onUpdate:modelValue": ($event: any) => ((formData[field.name]) = $event),
                    onChange: ($event: any) => (handleFieldChange(field, $event))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(CurrencyInput, {
                        ref_for: true,
                        ref_key: "currencyInputRefs",
                        ref: currencyInputRefs,
                        name: field.name,
                        disabled: isFieldDisabled(field),
                        mode: __props.mode,
                        initialValue: formData[field.name],
                        modelValue: formData[field.name],
                        "onUpdate:modelValue": ($event: any) => ((formData[field.name]) = $event),
                        onChange: ($event: any) => (handleFieldChange(field, $event))
                      }, null, 8, ["name", "disabled", "mode", "initialValue", "modelValue", "onUpdate:modelValue", "onChange"])
                    ]),
                    _: 2
                  }, 1032, ["name", "disabled", "mode", "modelValue", "onUpdate:modelValue", "onChange"]))
                : (field.maskType)
                  ? (_openBlock(), _createBlock(_unref(Field), {
                      key: 3,
                      id: field.name,
                      name: field.name,
                      type: field.type,
                      modelValue: formData[field.name],
                      "onUpdate:modelValue": [($event: any) => ((formData[field.name]) = $event), ($event: any) => (handleFieldChange(field, $event))],
                      disabled: isFieldDisabled(field)
                    }, null, 8, ["id", "name", "type", "modelValue", "onUpdate:modelValue", "disabled"]))
                  : (_openBlock(), _createBlock(_unref(Field), {
                      key: 4,
                      id: field.name,
                      name: field.name,
                      type: field.type,
                      modelValue: formData[field.name],
                      "onUpdate:modelValue": [($event: any) => ((formData[field.name]) = $event), ($event: any) => (handleFieldChange(field, $event))],
                      disabled: isFieldDisabled(field)
                    }, null, 8, ["id", "name", "type", "modelValue", "onUpdate:modelValue", "disabled"])),
          _withDirectives(_createVNode(_unref(ErrorMessage), {
            name: field.name,
            class: "error"
          }, null, 8, ["name"]), [
            [_vShow, __props.mode !== 'view']
          ])
        ], 2))
      }), 128)),
      (__props.loanDetails.isParentLOC)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(ParentAttestationConsent, {
              ref_key: "parentAttestationConsentRef",
              ref: parentAttestationConsentRef,
              modelValue: formData.parentConsent,
              "onUpdate:modelValue": [
                _cache[0] || (_cache[0] = ($event: any) => ((formData.parentConsent) = $event)),
                _cache[1] || (_cache[1] = ($event: any) => (formData.parentConsent = $event))
              ],
              disabled: __props.isSubmitting || __props.mode === _unref(FormModes).view || __props.mode === _unref(FormModes).edit,
              entityId: __props.draw?.id,
              entityTypeId: _unref(ConsentServiceEntityTypeIds).Draw,
              details: __props.draw?.parentAttestation || { consentGiven: false },
              mode: __props.mode
            }, null, 8, ["modelValue", "disabled", "entityId", "entityTypeId", "details", "mode"])
          ]))
        : _createCommentVNode("", true),
      (showOverdrawnWarning.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [...(_cache[2] || (_cache[2] = [
            _createElementVNode("span", { class: "error" }, "WARNING: You may overdraw this LOC limit. ", -1),
            _createElementVNode("span", null, "Please review all recent disbursements before placing a draw for this line of credit.", -1)
          ]))]))
        : _createCommentVNode("", true),
      (__props.mode !== 'view')
        ? (_openBlock(), _createElementBlock("button", {
            key: 2,
            class: "submit-button col-12",
            type: "submit",
            disabled: 
        !isFormValid.value(errors) ||
        __props.isSubmitting ||
        (__props.loanDetails.isParentLOC && formData.parentConsent !== true && __props.mode === _unref(FormModes).add)
      
          }, " Submit ", 8, _hoisted_5))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["validation-schema", "initial-values"]))
}
}

})